import { useSharedRollbar } from 'components/ErrorFallbackComponents/ErrorBoundary';
import React, {
  PropsWithChildren,
  Suspense,
  useContext,
  useEffect,
  useState
} from 'react';
import type { User } from 'firebase/auth';

export enum AuthState {
  PENDING,
  UNAUTHORIZED,
  AUTHORIZED
}

export interface FirebaseUser {
  name?: string;
  email?: string;
  is_subscriber?: boolean;
}

export const AuthContext = React.createContext<{
  authState: AuthState;
  user?: User;
  userPseudoId?: string | null;
  userName?: string;
  userEmail?: string;
  isSubscriber?: boolean;
  logOut: () => Promise<void>;
  hideSignupLink?: boolean;
  isLoading: boolean;
  changeHideSignupLinkStatus?: (value: boolean) => void;
}>({
  authState: AuthState.PENDING,
  logOut: () => Promise.resolve(),
  hideSignupLink: false,
  isLoading: true
});

const FirebaseAuthBootstrap = React.lazy(
  () => import('./FirebaseAuthBootstrap')
);

export const FirebaseAuthProvider: React.FC<PropsWithChildren> = ({
  children
}) => {
  const rollbar = useSharedRollbar();
  const [authState, setAuthState] = useState<AuthState>(AuthState.PENDING);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser>();
  const [user, setUser] = useState<User | null>();
  const [hideSignupLink, setHideSignupLink] = useState<boolean>(false);
  const [isAuthLoading, setIsAuthLoading] = useState<boolean>(true);
  const [isSessionLoading, setIsSessionLoading] = useState<boolean>(false);
  const [userPseudoId, setUserPseudoId] = useState<string | null | undefined>();
  const [isUserPseudoIdValidating, setIsUserPseudoIdValidating] =
    useState(false);
  const [isUserProfileLoading, setIsUserProfileLoading] = useState(false);

  useEffect(() => {
    if (rollbar && user) {
      rollbar.configure({
        payload: {
          ...(user ? { person: { id: user.uid } } : {})
        }
      });
    }
  }, [user, rollbar]);

  useEffect(() => {
    let mounted = true;
    let unsubscribe = () => {};

    import('lib/firebase/auth-core')
      .then(({ firebaseAuth }) => {
        if (!mounted) {
          return;
        }

        unsubscribe = firebaseAuth.onAuthStateChanged(nextUser => {
          if (!mounted) {
            return;
          }

          if (nextUser) {
            setUser(nextUser);
            setIsUserProfileLoading(true);
            setAuthState(AuthState.AUTHORIZED);
            setIsAuthLoading(false);
            return;
          }

          setUser(undefined);
          setFirebaseUser(undefined);
          setUserPseudoId(undefined);
          setIsUserProfileLoading(false);
          setAuthState(AuthState.UNAUTHORIZED);
          setIsAuthLoading(false);
        });
      })
      .catch(error => {
        console.warn('firebase.auth-core: ERROR', error);
        if (mounted) {
          setUser(undefined);
          setFirebaseUser(undefined);
          setUserPseudoId(undefined);
          setIsUserProfileLoading(false);
          setAuthState(AuthState.UNAUTHORIZED);
          setIsAuthLoading(false);
        }
      });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  const logOut = React.useCallback(async () => {
    const { logout } = await import('lib/firebase/auth');
    return logout().catch(err => console.warn(err));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user: user || undefined,
        isSubscriber: firebaseUser && firebaseUser.is_subscriber,
        userName: firebaseUser?.name ?? user?.displayName ?? undefined,
        userEmail: firebaseUser?.email ?? user?.email ?? undefined,
        authState,
        hideSignupLink,
        userPseudoId,
        changeHideSignupLinkStatus: setHideSignupLink,
        logOut,
        isLoading:
          isAuthLoading ||
          isSessionLoading ||
          isUserPseudoIdValidating ||
          isUserProfileLoading
      }}
    >
      {children}
      {authState !== AuthState.PENDING && (
        <Suspense fallback={<></>}>
          <FirebaseAuthBootstrap
            authState={authState}
            user={user}
            setFirebaseUser={setFirebaseUser}
            setIsSessionLoading={setIsSessionLoading}
            setIsUserProfileLoading={setIsUserProfileLoading}
            setIsUserPseudoIdValidating={setIsUserPseudoIdValidating}
            setUserPseudoId={setUserPseudoId}
          />
        </Suspense>
      )}
    </AuthContext.Provider>
  );
};

export const useFirebaseAuth = () => useContext(AuthContext);
